/**
 * Cesta: cantidades y eliminación sin recargar.
 *
 * Se usa /cart/change.js con el número de línea en lugar del id de variante:
 * si la misma variante entra dos veces con propiedades distintas, el id no la
 * identifica de forma única y se acabaría modificando la línea equivocada.
 *
 * Tras cada cambio se vuelve a pedir la sección al servidor y se sustituye el
 * marcado. Recalcular totales en el cliente parece más rápido pero se desvía
 * del servidor en cuanto hay descuentos automáticos o envíos condicionales.
 */

class ScCart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.onClick(event));

    const note = this.querySelector('[data-note]');
    if (note) {
      let timer;
      note.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.saveNote(note.value), 500);
      });
    }
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }

  onClick(event) {
    const up = event.target.closest('[data-qty-up]');
    const down = event.target.closest('[data-qty-down]');
    const remove = event.target.closest('[data-remove]');

    if (up) return this.step(up, 1);
    if (down) return this.step(down, -1);
    if (remove) return this.change(Number(remove.dataset.line), 0, remove);
  }

  step(button, delta) {
    const line = Number(button.dataset.line);
    const row = button.closest('.sc-cart-item');
    const current = Number(row?.querySelector('[data-qty-value]')?.textContent || 0);
    const next = current + delta;

    // Bajar de 1 equivale a quitar la pieza; es lo que espera quien pulsa "−".
    this.change(line, Math.max(0, next), button);
  }

  async change(line, quantity, trigger) {
    const row = trigger?.closest('.sc-cart-item');
    row?.classList.add('is-busy');

    try {
      const res = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });

      if (!res.ok) {
        row?.classList.remove('is-busy');
        return;
      }

      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:update'));
    } catch {
      row?.classList.remove('is-busy'); // sin red, se deja la fila usable
    }
  }

  /** Repinta la sección con el marcado que devuelve el servidor. */
  async refresh() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${this.sectionId}`);
      if (!res.ok) return window.location.reload();

      const html = await res.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('sc-cart');
      if (!fresh) return window.location.reload();

      this.innerHTML = fresh.innerHTML;
      this.dataset.itemCount = fresh.dataset.itemCount;
    } catch {
      window.location.reload();
    }
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '') || '';
  }

  async saveNote(value) {
    try {
      await fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: value }),
      });
    } catch {
      // La nota no bloquea la compra: un fallo aquí no debe interrumpir nada.
    }
  }
}

if (!customElements.get('sc-cart')) {
  customElements.define('sc-cart', ScCart);
}
